import { BillStatus } from "@redotech/cockroachdb-codegen/tables/bill-status";
import { BillType } from "@redotech/cockroachdb-codegen/tables/bill-type";
import { isEnumValue } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";
import { SubscriptionProduct } from "./unified-billing/enums";

export enum BillingChargeType {
  Coverage = "Coverage",
  Return = "Return",
  ShippingLabel = "ShippingLabel",
  Subscription = "Subscription",
  Campaign = "Campaign",
  OrderTracking = "OrderTracking",
  CheckoutOptimization = "CheckoutOptimization",
  Recover = "Recover",
  Credit = "Credit",
  Usage = "Usage",
}

export const chargeTypeLabels: Record<BillingChargeType, string> = {
  [BillingChargeType.Coverage]: "Coverage",
  [BillingChargeType.Return]: "Returns",
  [BillingChargeType.ShippingLabel]: "Shipping Labels",
  [BillingChargeType.Subscription]: "Subscription",
  [BillingChargeType.Campaign]: "Campaign",
  [BillingChargeType.OrderTracking]: "Order Tracking",
  [BillingChargeType.CheckoutOptimization]: "Checkout Optimization",
  [BillingChargeType.Recover]: "Recover",
  [BillingChargeType.Credit]: "Credit",
  [BillingChargeType.Usage]: "Usage",
};

export function getChargeTypeLabel(
  chargeType: BillingChargeType | string,
): string {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeLabels[chargeType as BillingChargeType];
  }
  return chargeType;
}

export enum ChargeSubtype {
  // Snake case values (legacy)
  ReturnCoverage = "return_coverage",
  PackageProtectionPlus = "package_protection_plus",
  FinalSaleReturnCoverage = "final_sale_return_coverage",
  ExtendedWarranty = "extended_warranty",
  UpsellRevenue = "upsell_revenue",
  ManagedClaimCredit = "managed_claim_credit",
  PerOrder = "per_order",
  PerSms = "per_sms",
  PerEmail = "per_email",
  MerchantCoveredLabel = "merchant_covered_label",
  MerchantCoveredLabelDamaged = "merchant_covered_label_damaged",
  MerchantCoveredLabelWrongProduct = "merchant_covered_label_wrong_product",
  MerchantCarrierLabelCredit = "merchant_carrier_label_credit",
  ShippingCostShare = "shipping_cost_share",
  ReturnLabelMarkupRevenue = "return_label_markup_revenue",
  CustomerPaidLabel = "customer_paid_label",
  PerReturnFee = "per_return_fee",
  ReturnsProcessingFee = "returns_processing_fee",
  FinalSaleCreditAdjustment = "final_sale_credit_adjustment",
  DutyDrawbackCredit = "duty_drawback_credit",
  // PascalCase values (from unified billing)
  ReturnCoveragePascal = "ReturnCoverage",
  PackageProtectionPlusPascal = "PackageProtectionPlus",
  FinalSaleReturnCoveragePascal = "FinalSaleReturnCoverage",
  ExtendedWarrantyPascal = "ExtendedWarranty",
  UpsellRevenuePascal = "UpsellRevenue",
  ManagedClaimCreditPascal = "ManagedClaimCredit",
  PerOrderPascal = "PerOrder",
  PerSmsPascal = "PerSms",
  PerEmailPascal = "PerEmail",
  MerchantCoveredLabelPascal = "MerchantCoveredLabel",
  MerchantCoveredLabelDamagedPascal = "MerchantCoveredLabelDamaged",
  MerchantCoveredLabelWrongProductPascal = "MerchantCoveredLabelWrongProduct",
  MerchantCarrierLabelCreditPascal = "MerchantCarrierLabelCredit",
  ShippingCostSharePascal = "ShippingCostShare",
  ReturnLabelMarkupRevenuePascal = "ReturnLabelMarkupRevenue",
  CustomerPaidLabelPascal = "CustomerPaidLabel",
  PerReturnFeePascal = "PerReturnFee",
  ReturnsProcessingFeePascal = "ReturnsProcessingFee",
  FinalSaleCreditAdjustmentPascal = "FinalSaleCreditAdjustment",
  DutyDrawbackCreditPascal = "DutyDrawbackCredit",
}

export const chargeSubtypeLabels: Record<ChargeSubtype, string> = {
  // Snake case labels
  [ChargeSubtype.ReturnCoverage]: "Return Coverage",
  [ChargeSubtype.PackageProtectionPlus]: "Package Protection Plus",
  [ChargeSubtype.FinalSaleReturnCoverage]: "Final Sale",
  [ChargeSubtype.ExtendedWarranty]: "Extended Warranty",
  [ChargeSubtype.UpsellRevenue]: "Upsell Revenue",
  [ChargeSubtype.ManagedClaimCredit]: "Managed Claim Credit",
  [ChargeSubtype.PerOrder]: "Per Order",
  [ChargeSubtype.PerSms]: "Per SMS",
  [ChargeSubtype.PerEmail]: "Per Email",
  [ChargeSubtype.MerchantCoveredLabel]: "Merchant Covered Label",
  [ChargeSubtype.MerchantCoveredLabelDamaged]: "Damaged Label",
  [ChargeSubtype.MerchantCoveredLabelWrongProduct]: "Wrong Product Label",
  [ChargeSubtype.MerchantCarrierLabelCredit]: "Carrier Label Credit",
  [ChargeSubtype.ShippingCostShare]: "Shipping Cost Share",
  [ChargeSubtype.ReturnLabelMarkupRevenue]: "Return Label Markup Revenue",
  [ChargeSubtype.CustomerPaidLabel]: "Customer Paid Label",
  [ChargeSubtype.PerReturnFee]: "Per Return Fee",
  [ChargeSubtype.ReturnsProcessingFee]: "Processing Fee",
  [ChargeSubtype.FinalSaleCreditAdjustment]: "Dynamic FSR Adjustment",
  [ChargeSubtype.DutyDrawbackCredit]: "Duty Drawback Credit",
  // PascalCase labels
  [ChargeSubtype.ReturnCoveragePascal]: "Return Coverage",
  [ChargeSubtype.PackageProtectionPlusPascal]: "Package Protection Plus",
  [ChargeSubtype.FinalSaleReturnCoveragePascal]: "Final Sale",
  [ChargeSubtype.ExtendedWarrantyPascal]: "Extended Warranty",
  [ChargeSubtype.UpsellRevenuePascal]: "Upsell Revenue",
  [ChargeSubtype.ManagedClaimCreditPascal]: "Managed Claim Credit",
  [ChargeSubtype.PerOrderPascal]: "Per Order",
  [ChargeSubtype.PerSmsPascal]: "Per SMS",
  [ChargeSubtype.PerEmailPascal]: "Per Email",
  [ChargeSubtype.MerchantCoveredLabelPascal]: "Merchant Covered Label",
  [ChargeSubtype.MerchantCoveredLabelDamagedPascal]: "Damaged Label",
  [ChargeSubtype.MerchantCoveredLabelWrongProductPascal]: "Wrong Product Label",
  [ChargeSubtype.MerchantCarrierLabelCreditPascal]: "Carrier Label Credit",
  [ChargeSubtype.ShippingCostSharePascal]: "Shipping Cost Share",
  [ChargeSubtype.ReturnLabelMarkupRevenuePascal]: "Return Label Markup Revenue",
  [ChargeSubtype.CustomerPaidLabelPascal]: "Customer Paid Label",
  [ChargeSubtype.PerReturnFeePascal]: "Per Return Fee",
  [ChargeSubtype.ReturnsProcessingFeePascal]: "Processing Fee",
  [ChargeSubtype.FinalSaleCreditAdjustmentPascal]: "Dynamic FSR Adjustment",
  [ChargeSubtype.DutyDrawbackCreditPascal]: "Duty Drawback Credit",
};

export function getChargeSubtypeLabel(subtype: ChargeSubtype | string): string {
  if (isEnumValue(subtype, ChargeSubtype)) {
    return chargeSubtypeLabels[subtype as ChargeSubtype];
  }
  return subtype;
}

export const billTypeLabels: Record<BillType, string> = {
  [BillType.Shipping]: "Shipping",
  [BillType.ReturnShipping]: "Returns",
  [BillType.Software]: "Software",
};

export function getBillTypeLabel(billType: BillType | string): string {
  if (isEnumValue(billType, BillType)) {
    return billTypeLabels[billType as BillType];
  }
  return billType;
}

export const billStatusLabels: Record<BillStatus, string> = {
  [BillStatus.Open]: "Open",
  [BillStatus.Closed]: "Pending",
  [BillStatus.Paid]: "Paid",
  [BillStatus.Void]: "Void",
  [BillStatus.Failed]: "Failed",
  [BillStatus.Refunded]: "Refunded",
  [BillStatus.PartiallyRefunded]: "Partially Refunded",
};

export type ChargeTypeBadgeColor =
  | "blueLight"
  | "blue"
  | "indigo"
  | "purple"
  | "pink"
  | "orange"
  | "brand"
  | "grayBlue"
  | "success"
  | "gray";

export const chargeTypeColors: Record<BillingChargeType, ChargeTypeBadgeColor> =
  {
    [BillingChargeType.Coverage]: "blueLight",
    [BillingChargeType.Return]: "blue",
    [BillingChargeType.ShippingLabel]: "indigo",
    [BillingChargeType.Subscription]: "purple",
    [BillingChargeType.Campaign]: "pink",
    [BillingChargeType.OrderTracking]: "orange",
    [BillingChargeType.CheckoutOptimization]: "brand",
    [BillingChargeType.Recover]: "grayBlue",
    [BillingChargeType.Credit]: "success",
    [BillingChargeType.Usage]: "orange",
  };

export function getChargeTypeColor(
  chargeType: BillingChargeType | string,
): ChargeTypeBadgeColor {
  if (isEnumValue(chargeType, BillingChargeType)) {
    return chargeTypeColors[chargeType as BillingChargeType];
  }
  return "gray";
}

// Subtypes are grouped by their parent charge type for consistent color coding.
const subtypeToChargeType: Record<ChargeSubtype, BillingChargeType> = {
  [ChargeSubtype.ReturnCoverage]: BillingChargeType.Coverage,
  [ChargeSubtype.PackageProtectionPlus]: BillingChargeType.Coverage,
  [ChargeSubtype.FinalSaleReturnCoverage]: BillingChargeType.Coverage,
  [ChargeSubtype.ExtendedWarranty]: BillingChargeType.Coverage,
  [ChargeSubtype.UpsellRevenue]: BillingChargeType.Coverage,
  [ChargeSubtype.ManagedClaimCredit]: BillingChargeType.Return,
  [ChargeSubtype.PerOrder]: BillingChargeType.Return,
  [ChargeSubtype.PerReturnFee]: BillingChargeType.Return,
  [ChargeSubtype.ReturnsProcessingFee]: BillingChargeType.Return,
  [ChargeSubtype.FinalSaleCreditAdjustment]: BillingChargeType.Coverage,
  [ChargeSubtype.DutyDrawbackCredit]: BillingChargeType.Return,
  [ChargeSubtype.MerchantCoveredLabel]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCoveredLabelDamaged]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCoveredLabelWrongProduct]:
    BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCarrierLabelCredit]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.ShippingCostShare]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.ReturnLabelMarkupRevenue]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.CustomerPaidLabel]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.PerSms]: BillingChargeType.Campaign,
  [ChargeSubtype.PerEmail]: BillingChargeType.Campaign,
  // PascalCase variants map to the same types
  [ChargeSubtype.ReturnCoveragePascal]: BillingChargeType.Coverage,
  [ChargeSubtype.PackageProtectionPlusPascal]: BillingChargeType.Coverage,
  [ChargeSubtype.FinalSaleReturnCoveragePascal]: BillingChargeType.Coverage,
  [ChargeSubtype.ExtendedWarrantyPascal]: BillingChargeType.Coverage,
  [ChargeSubtype.UpsellRevenuePascal]: BillingChargeType.Coverage,
  [ChargeSubtype.ManagedClaimCreditPascal]: BillingChargeType.Return,
  [ChargeSubtype.PerOrderPascal]: BillingChargeType.Return,
  [ChargeSubtype.PerReturnFeePascal]: BillingChargeType.Return,
  [ChargeSubtype.ReturnsProcessingFeePascal]: BillingChargeType.Return,
  [ChargeSubtype.FinalSaleCreditAdjustmentPascal]: BillingChargeType.Coverage,
  [ChargeSubtype.DutyDrawbackCreditPascal]: BillingChargeType.Return,
  [ChargeSubtype.MerchantCoveredLabelPascal]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCoveredLabelDamagedPascal]:
    BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCoveredLabelWrongProductPascal]:
    BillingChargeType.ShippingLabel,
  [ChargeSubtype.MerchantCarrierLabelCreditPascal]:
    BillingChargeType.ShippingLabel,
  [ChargeSubtype.ShippingCostSharePascal]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.ReturnLabelMarkupRevenuePascal]:
    BillingChargeType.ShippingLabel,
  [ChargeSubtype.CustomerPaidLabelPascal]: BillingChargeType.ShippingLabel,
  [ChargeSubtype.PerSmsPascal]: BillingChargeType.Campaign,
  [ChargeSubtype.PerEmailPascal]: BillingChargeType.Campaign,
};

export function getChargeSubtypeColor(
  subtype: ChargeSubtype | string,
): ChargeTypeBadgeColor {
  if (isEnumValue(subtype, ChargeSubtype)) {
    const parentType = subtypeToChargeType[subtype as ChargeSubtype];
    return chargeTypeColors[parentType];
  }
  return "gray";
}

export function getBillStatusLabel(status: BillStatus | string): string {
  if (isEnumValue(status, BillStatus)) {
    return billStatusLabels[status as BillStatus];
  }
  return status;
}

export type BillStatusTheme = "success" | "warning" | "error" | "default";

export const billStatusThemes: Record<BillStatus, BillStatusTheme> = {
  [BillStatus.Open]: "warning",
  [BillStatus.Closed]: "warning",
  [BillStatus.Paid]: "success",
  [BillStatus.Void]: "error",
  [BillStatus.Failed]: "error",
  [BillStatus.Refunded]: "success",
  [BillStatus.PartiallyRefunded]: "success",
};

export function getBillStatusTheme(
  status: BillStatus | string,
): BillStatusTheme {
  if (isEnumValue(status, BillStatus)) {
    return billStatusThemes[status as BillStatus];
  }
  return "default";
}

/**
 * The status a merchant actually sees, derived from the persisted `BillStatus`
 * plus whether the bill has any billing transaction. The persisted status
 * cannot distinguish a closed bill that is awaiting its first (manual)
 * collection from one whose charge is in flight — both sit at `Closed` — so the
 * display layer splits them: a closed bill with no transaction is `New`
 * (awaiting payment, payable now), one with a transaction is `Pending`
 * (in-flight, e.g. an ACH charge settling). This is the single source of truth
 * for both the unified invoices UI and the legacy payouts status
 * (`payoutStatusFromBill`).
 */
export enum BillDisplayStatus {
  New = "New",
  Open = "Open",
  Pending = "Pending",
  Paid = "Paid",
  Void = "Void",
  Failed = "Failed",
  Refunded = "Refunded",
  PartiallyRefunded = "PartiallyRefunded",
  // A closed period that netted a credit (merchant owed): nothing was charged
  // and the negative balance carries to the next period. Distinct from `New`
  // (which is a closed-but-unpaid amount OWED, awaiting payment).
  Credit = "Credit",
}

export function deriveBillDisplayStatus(
  status: BillStatus,
  hasTransactions: boolean,
  // The bill's net balance. A closed bill with no real transaction and a
  // negative balance is a CREDIT (carried forward), not an amount-owed `New`.
  balanceEnd?: number,
): BillDisplayStatus {
  switch (status) {
    case BillStatus.Open:
      return BillDisplayStatus.Open;
    case BillStatus.Closed:
      if (hasTransactions) {
        return BillDisplayStatus.Pending;
      }
      return balanceEnd !== undefined && balanceEnd < 0
        ? BillDisplayStatus.Credit
        : BillDisplayStatus.New;
    case BillStatus.Paid:
      return BillDisplayStatus.Paid;
    case BillStatus.Void:
      return BillDisplayStatus.Void;
    case BillStatus.Failed:
      return BillDisplayStatus.Failed;
    case BillStatus.Refunded:
      return BillDisplayStatus.Refunded;
    case BillStatus.PartiallyRefunded:
      return BillDisplayStatus.PartiallyRefunded;
    default:
      return assertNever(status);
  }
}

/**
 * The unified billing cycle pre-allocates a `pending` transaction per
 * (account, bill) at period start as a stable idempotency anchor. Until the
 * provider charge fires it carries no charge id and represents no real attempt
 * — it must not count as a transaction when deriving display status, or a bill
 * awaiting its first (manual) charge would read as "Pending" instead of "New".
 * Shared so the unified invoices UI and the legacy payout status derive
 * `hasTransactions` identically and never drift.
 */
export function isUnchargedPlaceholderTxn(txn: {
  status: string;
  stripe_charge_id?: string | null;
  shopify_charge_id?: string | null;
  idempotency_key?: string | null;
}): boolean {
  return (
    txn.status === TransactionStatus.Pending &&
    txn.stripe_charge_id == null &&
    txn.shopify_charge_id == null &&
    (txn.idempotency_key?.startsWith("period-pending:") ?? false)
  );
}

export const billDisplayStatusLabels: Record<BillDisplayStatus, string> = {
  [BillDisplayStatus.New]: "New",
  [BillDisplayStatus.Open]: "Open",
  [BillDisplayStatus.Pending]: "Pending",
  [BillDisplayStatus.Paid]: "Paid",
  [BillDisplayStatus.Void]: "Void",
  [BillDisplayStatus.Failed]: "Failed",
  [BillDisplayStatus.Refunded]: "Refunded",
  [BillDisplayStatus.PartiallyRefunded]: "Partially Refunded",
  [BillDisplayStatus.Credit]: "Credit",
};

export const billDisplayStatusThemes: Record<
  BillDisplayStatus,
  BillStatusTheme
> = {
  [BillDisplayStatus.New]: "warning",
  [BillDisplayStatus.Open]: "warning",
  [BillDisplayStatus.Pending]: "warning",
  [BillDisplayStatus.Paid]: "success",
  [BillDisplayStatus.Void]: "error",
  [BillDisplayStatus.Failed]: "error",
  [BillDisplayStatus.Refunded]: "success",
  [BillDisplayStatus.PartiallyRefunded]: "success",
  [BillDisplayStatus.Credit]: "success",
};

export enum TransactionStatus {
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed",
}

export const transactionStatusLabels: Record<TransactionStatus, string> = {
  [TransactionStatus.Pending]: "Pending Payment",
  [TransactionStatus.Success]: "Payment",
  [TransactionStatus.Failed]: "Failed Payment",
};

export function getTransactionStatusLabel(
  status: TransactionStatus | string,
): string {
  if (isEnumValue(status, TransactionStatus)) {
    return transactionStatusLabels[status as TransactionStatus];
  }
  return status;
}

export enum ShopifySubscriptionStatus {
  ACTIVE = "ACTIVE",
  CANCELLED = "CANCELLED",
  DECLINED = "DECLINED",
  EXPIRED = "EXPIRED",
  FROZEN = "FROZEN",
  PENDING = "PENDING",
  ACCEPTED = "ACCEPTED",
}

export const shopifySubscriptionStatusLabels: Record<
  ShopifySubscriptionStatus,
  string
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "Active",
  [ShopifySubscriptionStatus.CANCELLED]: "Cancelled",
  [ShopifySubscriptionStatus.DECLINED]: "Declined",
  [ShopifySubscriptionStatus.EXPIRED]: "Expired",
  [ShopifySubscriptionStatus.FROZEN]: "Frozen",
  [ShopifySubscriptionStatus.PENDING]: "Pending",
  [ShopifySubscriptionStatus.ACCEPTED]: "Accepted",
};

export function getShopifySubscriptionStatusLabel(
  status: ShopifySubscriptionStatus | string,
): string {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusLabels[status as ShopifySubscriptionStatus];
  }
  return status;
}

export type ShopifySubscriptionStatusColor =
  | "success"
  | "warning"
  | "error"
  | "gray";

export const shopifySubscriptionStatusColors: Record<
  ShopifySubscriptionStatus,
  ShopifySubscriptionStatusColor
> = {
  [ShopifySubscriptionStatus.ACTIVE]: "success",
  [ShopifySubscriptionStatus.CANCELLED]: "error",
  [ShopifySubscriptionStatus.DECLINED]: "error",
  [ShopifySubscriptionStatus.EXPIRED]: "gray",
  [ShopifySubscriptionStatus.FROZEN]: "warning",
  [ShopifySubscriptionStatus.PENDING]: "warning",
  [ShopifySubscriptionStatus.ACCEPTED]: "success",
};

export function getShopifySubscriptionStatusColor(
  status: ShopifySubscriptionStatus | string,
): ShopifySubscriptionStatusColor {
  if (isEnumValue(status, ShopifySubscriptionStatus)) {
    return shopifySubscriptionStatusColors[status as ShopifySubscriptionStatus];
  }
  return "gray";
}

export const subscriptionProductLabels: Record<SubscriptionProduct, string> = {
  [SubscriptionProduct.MarketingSms]: "Marketing SMS",
  [SubscriptionProduct.MarketingEmail]: "Marketing Email",
  [SubscriptionProduct.OrderTracking]: "Order Tracking",
  [SubscriptionProduct.ReturnAiExchange]: "AI Exchange",
  [SubscriptionProduct.Returns]: "Returns",
  [SubscriptionProduct.CheckoutOptimization]: "Checkout Optimization",
  [SubscriptionProduct.Aeo]: "AEO",
  [SubscriptionProduct.Ims]: "IMS",
  [SubscriptionProduct.ImsForecasting]: "IMS Forecasting",
  [SubscriptionProduct.Recover]: "Recover",
  [SubscriptionProduct.SupportBase]: "Support",
  [SubscriptionProduct.SupportAi]: "Support AI",
  [SubscriptionProduct.SupportSms]: "Support SMS",
  [SubscriptionProduct.SupportVoice]: "Support Voice",
  [SubscriptionProduct.Oms]: "OMS",
  [SubscriptionProduct.Wms]: "WMS",
  [SubscriptionProduct.Disputes]: "Disputes",
  [SubscriptionProduct.Lcg]: "LCG",
};

export function getSubscriptionProductLabel(
  product: SubscriptionProduct | string,
): string {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductLabels[product as SubscriptionProduct];
  }
  return product;
}

export type SubscriptionProductBadgeColor = ChargeTypeBadgeColor;

export const subscriptionProductColors: Record<
  SubscriptionProduct,
  SubscriptionProductBadgeColor
> = {
  [SubscriptionProduct.MarketingSms]: "pink",
  [SubscriptionProduct.MarketingEmail]: "pink",
  [SubscriptionProduct.OrderTracking]: "orange",
  [SubscriptionProduct.ReturnAiExchange]: "blue",
  [SubscriptionProduct.Returns]: "blue",
  [SubscriptionProduct.CheckoutOptimization]: "orange",
  [SubscriptionProduct.Aeo]: "brand",
  [SubscriptionProduct.Ims]: "indigo",
  [SubscriptionProduct.ImsForecasting]: "indigo",
  [SubscriptionProduct.Recover]: "grayBlue",
  [SubscriptionProduct.SupportBase]: "purple",
  [SubscriptionProduct.SupportAi]: "purple",
  [SubscriptionProduct.SupportSms]: "purple",
  [SubscriptionProduct.SupportVoice]: "purple",
  [SubscriptionProduct.Oms]: "blueLight",
  [SubscriptionProduct.Wms]: "blueLight",
  [SubscriptionProduct.Disputes]: "success",
  [SubscriptionProduct.Lcg]: "blueLight",
};

export function getSubscriptionProductColor(
  product: SubscriptionProduct | string,
): SubscriptionProductBadgeColor {
  if (isEnumValue(product, SubscriptionProduct)) {
    return subscriptionProductColors[product as SubscriptionProduct];
  }
  return "gray";
}

export enum PaymentProvider {
  Stripe = "Stripe",
  Shopify = "Shopify",
}

export enum PaymentMethodType {
  Card = "Card",
  UsBankAccount = "UsBankAccount",
  ShopifyAppSubscription = "ShopifyAppSubscription",
}

export enum PaymentMethodPriority {
  Primary = "Primary",
  Secondary = "Secondary",
  ShopifySubscription = "ShopifySubscription",
  Deactivated = "Deactivated",
}
